import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { authGet } from "../../lib";
import { Navigation } from "../../lib";

const useStyles = makeStyles((theme) => ({
  root: { maxWidth: 1200, width: "100%", margin: "0 auto", padding: theme.spacing(3) },
  backBtn: { marginBottom: theme.spacing(2), textTransform: "none" },
  summaryCard: {
    padding: theme.spacing(2.5, 3),
    marginBottom: theme.spacing(3),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(4),
    flexWrap: "wrap",
    boxShadow: "none",
    border: "1px solid rgba(0,0,0,0.09)",
  },
  summaryField: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  summaryLabel: {
    fontSize: "0.7rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: theme.palette.text.secondary,
  },
  summaryValue: {
    fontSize: "0.95rem",
    color: theme.palette.text.primary,
  },
  errorText: { color: "#c62828", fontSize: "0.8rem", maxWidth: 400, wordBreak: "break-word" },
}));

const RUN_STATUS_COLORS = {
  success:         { label: "Success",         style: { backgroundColor: "#e8f5e9", color: "#2e7d32" } },
  partial_failure: { label: "Partial Failure",  style: { backgroundColor: "#fff3e0", color: "#e65100" } },
  failed:          { label: "Failed",           style: { backgroundColor: "#ffebee", color: "#c62828" } },
  running:         { label: "Running",          style: { backgroundColor: "#e3f2fd", color: "#1565c0" } },
};

const ITEM_STATUS_COLORS = {
  success: { label: "Success", style: { backgroundColor: "#e8f5e9", color: "#2e7d32" } },
  failed:  { label: "Failed",  style: { backgroundColor: "#ffebee", color: "#c62828" } },
};

function formatDuration(ms) {
  if (ms == null) return "â€”";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const m = Math.floor(ms / 60000);
  const s = Math.round((ms % 60000) / 1000);
  return `${m}m ${s}s`;
}

function formatDate(val) {
  if (!val) return "â€”";
  return new Date(val).toLocaleString();
}

export function MagentoSyncDetailPage(props) {
  const classes = useStyles();
  const runId = props.pageRoute?.match?.params?.run_id;

  const [run, setRun]         = useState(null);
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack]     = useState({ open: false, msg: "" });

  useEffect(() => {
    if (!runId) return;
    setLoading(true);
    authGet(`/magento-sync/runs/${runId}`).then(({ data, error }) => {
      setLoading(false);
      if (error) { setSnack({ open: true, msg: error?.message || "Failed to load run" }); return; }
      setRun(data?.run ?? null);
      setItems(Array.isArray(data?.items) ? data.items : []);
    });
  }, [runId]);

  if (loading) {
    return (
      <Box className={classes.root}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const runSc = run ? (RUN_STATUS_COLORS[run.status] || RUN_STATUS_COLORS.running) : null;

  return (
    <Box className={classes.root}>
      <Button
        size="small"
        variant="outlined"
        className={classes.backBtn}
        onClick={() => Navigation.go("/magento-sync")}
      >
        ← Back to Sync Log
      </Button>

      <Typography variant="h6" gutterBottom>
        Sync Run #{runId}
      </Typography>

      {run && (
        <Paper className={classes.summaryCard}>
          <Box className={classes.summaryField}>
            <Typography className={classes.summaryLabel}>Run #</Typography>
            <Typography className={classes.summaryValue}>{run.id}</Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box className={classes.summaryField}>
            <Typography className={classes.summaryLabel}>Started</Typography>
            <Typography className={classes.summaryValue}>{formatDate(run.started_at)}</Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box className={classes.summaryField}>
            <Typography className={classes.summaryLabel}>Duration</Typography>
            <Typography className={classes.summaryValue}>{formatDuration(run.duration_ms)}</Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box className={classes.summaryField}>
            <Typography className={classes.summaryLabel}>Items</Typography>
            <Typography className={classes.summaryValue}>
              {[
                run.items_created > 0 && `${run.items_created} created`,
                run.items_updated > 0 && `${run.items_updated} updated`,
              ].filter(Boolean).join(", ") || "-"}
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box className={classes.summaryField}>
            <Typography className={classes.summaryLabel}>Result</Typography>
            <Box style={{ marginTop: 2 }}>
              <Chip
                size="small"
                label={`${runSc.label} (${run.items_succeeded}/${run.items_attempted})`}
                style={runSc.style}
              />
            </Box>
          </Box>
        </Paper>
      )}

      <Typography variant="subtitle1" gutterBottom>
        Items in this run
      </Typography>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Item ID</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>HTTP</TableCell>
              <TableCell>Error</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => {
              const sc = ITEM_STATUS_COLORS[item.status] || ITEM_STATUS_COLORS.failed;
              return (
                <TableRow key={item.id} hover>
                  <TableCell>{item.item_id}</TableCell>
                  <TableCell style={{ textTransform: "capitalize" }}>{item.action}</TableCell>
                  <TableCell>
                    <Chip size="small" label={sc.label} style={sc.style} />
                  </TableCell>
                  <TableCell>{item.http_status ?? "Unknown Status"}</TableCell>
                  <TableCell className={item.error_message ? classes.errorText : undefined}>
                    {item.error_message || ""}
                  </TableCell>
                </TableRow>
              );
            })}
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" style={{ color: "#999", padding: 24 }}>
                  No items recorded
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        message={snack.msg}
      />
    </Box>
  );
}